import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Permission } from '@pirochi/shared';
import { api, onUnauthorized, setCsrfToken } from '@/services/api';

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  roleKey: string;
  permissions: string[];
}

interface AuthState {
  user: SessionUser | null;
  loading: boolean;
  login(email: string, password: string): Promise<void>;
  logout(): Promise<void>;
  can(p: Permission | Permission[]): boolean;
  refresh(): Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const qc = useQueryClient();

  const refresh = useCallback(async () => {
    try {
      const r = await api.get<{ user: SessionUser; csrfToken: string }>('/auth/me');
      setCsrfToken(r.data.csrfToken);
      setUser(r.data.user);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    return onUnauthorized(() => {
      setUser(null);
      qc.clear();
    });
  }, [refresh, qc]);

  const value: AuthState = {
    user,
    loading,
    refresh,
    async login(email, password) {
      const r = await api.post<{ user: SessionUser; csrfToken: string }>('/auth/login', { email, password });
      setCsrfToken(r.data.csrfToken);
      qc.clear();
      setUser(r.data.user);
    },
    async logout() {
      try {
        await api.post('/auth/logout');
      } finally {
        setCsrfToken('');
        qc.clear();
        setUser(null);
      }
    },
    can(p) {
      if (!user) return false;
      const list = Array.isArray(p) ? p : [p];
      return list.every((x) => user.permissions.includes(x));
    },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}
